"use client";

import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api/client";
import type { SkillTreeGoal } from "@/app/api/skill-tree/goals/route";

const TYPE_ICON: Record<string, string> = {
  hand_limit:      "✋",
  adventure_limit: "🧭",
  spirit:          "✨",
  material:        "💎",
  background:      "🏛️",
};

function ConditionBar({ label, unit, current, threshold, done }: {
  label: string; unit: string; current: number; threshold: number; done: boolean;
}) {
  if (threshold === 0) return null; // free 条件は非表示
  const pct = Math.min(Math.round((current / threshold) * 100), 100);
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[11px]">
        <span className={done ? "text-emerald-400" : "text-[var(--foreground)] opacity-60"}>{label}</span>
        <span className={done ? "text-emerald-400" : "text-[var(--foreground)] opacity-50"}>
          {current}/{threshold}{unit}
        </span>
      </div>
      <div className="h-1 w-full rounded-full bg-white/10">
        <div
          className={`h-1 rounded-full transition-all ${done ? "bg-emerald-400" : "bg-violet-400"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function GoalCard({ goal, rank }: { goal: SkillTreeGoal; rank: number }) {
  const visibleConds = goal.conditions.filter((c) => c.threshold > 0);
  return (
    <div className={`rounded-xl border p-3 space-y-2 transition-all ${
      goal.isReady
        ? "border-emerald-500/60 bg-emerald-500/10"
        : rank === 0
          ? "border-violet-500/50 bg-[var(--background)]/65 backdrop-blur-sm"
          : "border-white/15 bg-[var(--background)]/50 backdrop-blur-sm"
    }`}>
      {/* ヘッダー */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base shrink-0">{TYPE_ICON[goal.type] ?? "🔓"}</span>
          <div className="min-w-0">
            <p className={`text-sm font-semibold truncate ${
              goal.isReady ? "text-emerald-300" : "text-[var(--foreground)]"
            }`}>{goal.name}</p>
            <p className="text-[11px] text-[var(--foreground)] opacity-50 truncate">{goal.rewardLabel}</p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {goal.isReady ? (
            <span className="text-[11px] font-bold text-emerald-400 bg-emerald-400/20 px-2 py-0.5 rounded-full">達成！</span>
          ) : (
            <span className="text-sm font-bold text-violet-300">{goal.progress}%</span>
          )}
        </div>
      </div>

      {/* 全体プログレスバー */}
      {!goal.isReady && (
        <div className="h-1.5 w-full rounded-full bg-white/10">
          <div
            className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-indigo-400 transition-all"
            style={{ width: `${goal.progress}%` }}
          />
        </div>
      )}

      {/* 条件詳細 */}
      {visibleConds.length > 0 && (
        <div className="space-y-1.5">
          {visibleConds.map((c, i) => (
            <ConditionBar key={i} {...c} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function SkillTreeGoals({ token }: { token: string }) {
  const [goals, setGoals] = useState<SkillTreeGoal[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ goals: SkillTreeGoal[] }>("/api/skill-tree/goals", token)
      .then((r) => setGoals((r?.goals ?? []).filter((g) => !g.isUnlocked)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return null;
  if (goals.length === 0) return null;

  const shown = expanded ? goals : goals.slice(0, 3);
  const readyCount = goals.filter((g) => g.isReady).length;

  return (
    <div className="w-full max-w-md space-y-2">
      {/* セクションヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[var(--foreground)] opacity-90">解放目標</p>
          {readyCount > 0 && (
            <p className="text-[11px] text-emerald-400">{readyCount}件が達成済み（次のアクションで解放）</p>
          )}
        </div>
        <span className="text-[11px] text-[var(--foreground)] opacity-40">{goals.length}件</span>
      </div>

      {/* ゴール一覧 */}
      <div className="space-y-1.5">
        {shown.map((g, i) => (
          <GoalCard key={g.id} goal={g} rank={i} />
        ))}
      </div>

      {/* 展開ボタン */}
      {goals.length > 3 && (
        <button
          onClick={() => setExpanded((p) => !p)}
          className="w-full text-center text-sm text-[var(--foreground)] opacity-40 hover:opacity-70 py-1 transition-opacity"
        >
          {expanded ? "▲ 折りたたむ" : `▼ 残り${goals.length - 3}件を表示`}
        </button>
      )}
    </div>
  );
}
